const DEFAULT_CHUNK_SIZE = 2 * 1024 * 1024;

function isVideoFile(file) {
    const mime = String(file?.type || '').toLowerCase();
    const name = String(file?.name || '').toLowerCase();

    return mime.startsWith('video/')
        || ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.m3u8'].some((ext) => name.endsWith(ext));
}

function createUploadId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        return crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === 'x' ? r : (r & 0x3) | 0x8;
        return v.toString(16);
    });
}

async function fetchUploadConfig(host) {
    const token = localStorage.getItem('token');
    const res = await fetch(`${host}/teacher/courses/files/upload-config`, {
        headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
    });
    if (res.status === 200) {
        const json = await res.json();
        return json?.data || {};
    }
    return { chunk_size: DEFAULT_CHUNK_SIZE };
}

export async function uploadVideoInChunks({
    host,
    file,
    courseId,
    onProgress,
}) {
    const config = await fetchUploadConfig(host);
    const chunkSize = config.chunk_size || DEFAULT_CHUNK_SIZE;
    const totalChunks = Math.max(1, Math.ceil(file.size / chunkSize));
    const uploadId = createUploadId();
    const token = localStorage.getItem('token');

    for (let index = 0; index < totalChunks; index++) {
        const start = index * chunkSize;
        const end = Math.min(start + chunkSize, file.size);
        const blob = file.slice(start, end);

        const formData = new FormData();
        formData.append('upload_id', uploadId);
        formData.append('chunk_index', String(index));
        formData.append('total_chunks', String(totalChunks));
        formData.append('course_id', String(courseId));
        formData.append('original_name', file.name);
        formData.append('chunk', blob, `${file.name}.part${index}`);

        const response = await fetch(`${host}/teacher/courses/files/upload-chunk`, {
            method: 'POST',
            headers: {
                Authorization: `Bearer ${token}`,
                Accept: 'application/json',
            },
            body: formData,
        });

        const json = await response.json().catch(() => ({}));
        if (response.status !== 200) {
            throw new Error(json?.errors?.[0] || json?.message || `Chunk ${index + 1} failed`);
        }

        onProgress?.(Math.round(((index + 1) / totalChunks) * 92));
    }

    return { uploadId, totalChunks, transcodeAvailable: config.transcode_available };
}

export async function completeVideoUpload({
    host,
    uploadId,
    courseId,
    nameEn,
    nameAr,
    thumbnail,
    onProgress,
}) {
    const token = localStorage.getItem('token');
    const formData = new FormData();
    formData.append('upload_id', uploadId);
    formData.append('course_id', String(courseId));
    formData.append('name_en', nameEn);
    formData.append('name_ar', nameAr);
    if (thumbnail) formData.append('thumbnail', thumbnail);

    onProgress?.(95);

    const response = await fetch(`${host}/teacher/courses/files/upload-complete`, {
        method: 'POST',
        headers: {
            Authorization: `Bearer ${token}`,
            Accept: 'application/json',
        },
        body: formData,
    });

    const json = await response.json().catch(() => ({}));
    if (response.status !== 200) {
        throw new Error(json?.errors?.[0] || json?.message || 'Upload complete failed');
    }

    onProgress?.(100);
    return json?.data;
}

export async function uploadCourseVideo({
    host,
    file,
    courseId,
    nameEn,
    nameAr,
    thumbnail,
    onProgress,
}) {
    const CHUNK_THRESHOLD = 8 * 1024 * 1024;

    if (!isVideoFile(file) || file.size <= CHUNK_THRESHOLD) {
        onProgress?.(10);
        const formData = new FormData();
        formData.append('name_en', nameEn);
        formData.append('name_ar', nameAr);
        formData.append('course_id', courseId);
        formData.append('file', file);
        if (thumbnail) formData.append('thumbnail', thumbnail);

        const token = localStorage.getItem('token');
        const response = await fetch(`${host}/teacher/courses/files/store`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, Accept: 'application/json' },
            body: formData,
        });
        const json = await response.json().catch(() => ({}));
        if (response.status !== 200) {
            throw new Error(json?.errors?.[0] || json?.message || 'Upload failed');
        }
        onProgress?.(100);
        return json?.data;
    }

    const { uploadId } = await uploadVideoInChunks({ host, file, courseId, onProgress });
    return completeVideoUpload({
        host,
        uploadId,
        courseId,
        nameEn,
        nameAr,
        thumbnail,
        onProgress,
    });
}
